import { FilterType } from '@protos/filter';
import { BlotterWidget } from '@shared/components/BlotterWidget';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useAdminApi } from '@shared/hooks/useAdminApi';
import { useOrganizationOptions } from '@shared/hooks/useOrganizationOptions';
import { USER_COLUMNS, USER_FILTERS } from '@shared/protos/user';
import { checkIsFluxAdminUser, checkIsTradingAdminUser, checkIsAdminUser, checkIsAdminReadOnlyUser } from '@utils/permissions';
import { ColDef } from 'ag-grid-enterprise';
import { useEffect, useMemo, useState } from 'react';

import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';

const getRowId = ({ data }): string => data?.id?.toString();

const defaultColDef = (navigate: NavigateFunction, isAllowedUserAccess: boolean) => {
  return {
    onCellClicked(event) {
      if (event.column.getColId() === 'id' && isAllowedUserAccess) {
        navigate(`/users/${event.data.id}`);
      }
    },
    cellStyle(params) {
      if (params.column.getColId() === 'id') {
        return { cursor: 'pointer' };
      }
      return null;
    },
  } as ColDef;
};

export const Users = () => {
  const { apiClient } = useAdminApi();
  const dataLoader = apiClient?.usersLoader();
  const navigate = useNavigate();
  const user = useUserContext();
  const { organizations, loading: organizationsLoading } = useOrganizationOptions();
  const [searchParams] = useSearchParams();
  const accountName = searchParams.get('account_name') ?? '';

  // GET /users joins organizations, so `account_name` filters on organizations.name.
  // The options come from the roster because the API matches the name exactly.
  //
  // Offered only once the roster is in. FilterPanel treats a URL value that is not in
  // `options` as invalid and resets every search param, so an empty options list would
  // wipe ?account_name and any other filter alongside it on load. The current value is
  // seeded in for the same reason: an organization the roster does not carry would
  // otherwise take every other filter down with it. One gap remains - a name containing
  // a comma, which useURLSearchParams splits into an array that no string option matches.
  const filters = useMemo(() => {
    if (!organizations.length) return USER_FILTERS;
    const names = organizations.map(organization => organization.name);
    return [
      ...USER_FILTERS,
      {
        type: FilterType.AUTOCOMPLETE,
        name: 'account_name',
        label: 'Organization',
        options: accountName && !names.includes(accountName) ? [accountName, ...names] : names,
      },
    ];
  }, [organizations, accountName]);
  const isAllowedUserAccess = !!(
    checkIsAdminUser(user) ||
    checkIsAdminReadOnlyUser(user) ||
    checkIsTradingAdminUser(user) ||
    checkIsFluxAdminUser(user)
  );

  const [gridHeight, setGridHeight] = useState(() => {
    return document.documentElement.clientHeight - 100;
  });

  useEffect(() => {
    const updateHeight = () => {
      setGridHeight(document.documentElement.clientHeight - 100);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  // Gated on the roster because `filters` is serialised into useURLFilteredData's change
  // key: letting the options fill in after first paint re-runs fetchData, which calls
  // resetQuery and drops every page already scrolled in. The real cost here is that the
  // roster is fetched whole, which is what wants fixing rather than this gate.
  if (!dataLoader || organizationsLoading) return null;

  return (
    <BlotterWidget
      dataLoader={dataLoader}
      columnDefs={USER_COLUMNS}
      getRowId={getRowId}
      maxHeight={gridHeight}
      filters={filters}
      defaultColDef={defaultColDef(navigate, isAllowedUserAccess)}
      autoSizeStrategy={{ type: 'fitGridWidth' }}
    />
  );
};
